/* The exercise library — the head, the filter disclosure, the active selections and the list.
 *
 * Every block here was `features/exercise-library-rail/ui/ExerciseRail.module.css`'s until
 * 17 September 2026, and moved down with the markup when the survey editor's recording picker became
 * the same library in a dialog. The rail's own chrome — its box, its eyebrow, its title and ✕, its
 * day-fit caption and its foot — stayed behind, because none of it is the library.
 *
 ── The two widths, and where each one comes from ───────────────────────────────────────────
 * · `drawer`   320px, the 07.09 programator's drawer. The full chrome, and since 19 September 2026
 *              the calendar's day dock draws the same one — a 240px `compact` variant stood there
 *              until then and left with its last host.
 * · `dialog`   the survey editor's recording picker. The drawer's chrome inside a modal whose box
 *              and padding belong to `PickerDialog`, so the head here carries none of its own.
 *
 * ── The rail has no padding of its own, and that is structural ───────────────────────────────
 * The gallery builds it as stacked blocks each carrying its own padding and its own hairline, so the
 * hairlines run the full width rather than stopping short of a gutter. A padded container with gaps
 * between the blocks cannot draw that, which is what an earlier transcription got wrong. */

.library {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

/* ── The head block: whatever the host puts above, the search, the filters, the selections ── */

.header {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-9px);
  padding: var(--space-3) var(--space-14px) var(--space-11px);
  border-bottom: var(--border-hairline) solid var(--border);
}

.drawer .header {
  gap: var(--space-2);
  padding: var(--space-11px) var(--space-11px) var(--space-9px);
}

/* The dialog's popup already pads itself and draws its own head above this one, so the library's
 * head carries neither padding nor a rule — it is the search row and what the filters put under it. */
.dialog .header {
  gap: var(--space-2);
  padding: var(--space-0);
  border-bottom: 0;
}

/* ── Search, and the filter disclosure beside it ─────────────────────────────────────────── */

.searchRow {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
}

.search {
  display: flex;
  flex: 1;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
  height: 36px;
  padding: var(--space-0) var(--space-3);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-control);
}

.drawer .search {
  gap: var(--space-7px);
  height: 30px;
  padding: var(--space-0) var(--space-9px);
  border-radius: var(--radius-11px);
}

/* The field is the mock's own 34x34 row: it stands beside a 34px control, and a field four pixels
 * shorter than the button next to it is the pairing that made the button look bolted on. */
.drawer .searchRow,
.dialog .searchRow {
  gap: var(--space-2);
}

.drawer .search,
.dialog .search {
  gap: var(--space-2);
  height: 34px;
  padding: var(--space-0) var(--space-11px);
}

.search:focus-within {
  box-shadow: var(--ring-field);
}

/* The DRAWER's field is focused by the surface, not by the trainer: it takes focus the moment the
 * library opens so a name can be typed straight away. The app's field ring sits OUTSIDE the border,
 * so that arrival drew two concentric rings, and in the dark theme both are white on a 320px column
 * — the owner's word for it was unprintable. One ring: the border itself goes to `--text` and the
 * shadow is dropped. Focus stays as visible as it was, and the drawer opens looking finished. */
.drawer .search:focus-within {
  box-shadow: none;
  border-color: var(--text);
}

.searchIcon {
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
  fill: none;
  stroke: var(--text-2);
  stroke-width: 2;
  stroke-linecap: round;
}

.drawer .searchIcon {
  width: 13px;
  height: 13px;
}

.searchInput {
  width: 100%;
  min-width: 0;
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text);
  background: transparent;
  border: 0;
}

.drawer .searchInput {
  font-size: var(--fs-micro);
}

.searchInput:focus-visible {
  outline: none;
}

/* Chrome draws its own clear affordance inside a `type="search"` field, in ITS blue. The gallery
 * draws no such control, and a coloured glyph the app never chose is exactly what the accent rule
 * exists to keep off the screen. The field is cleared by selecting and deleting, as every other
 * field in this app is. */
.searchInput::-webkit-search-cancel-button {
  appearance: none;
}

/* The gallery's `Filtry ③` — one disclosure over the whole facet panel. Only the GLYPH form below
 * has a host today; this is the base the glyph narrows, and it is what a labelled variant would
 * take if a width ever asked for the word again. */
.filterToggle {
  display: inline-flex;
  flex: none;
  gap: var(--space-7px);
  align-items: center;
  height: 36px;
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text);
  white-space: nowrap;
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-control);
}

.filterToggle:hover {
  background: var(--bg-inset);
}

.filterToggle:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.filterIcon {
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
  fill: none;
  stroke: var(--text);
  stroke-width: 2;
  stroke-linecap: round;
}

/* The GLYPH form of the filter control — measured off the 07.09 mock rather than inherited from the
 * 460px button above it. 320px has no room for the word next to a search field, so the name moves
 * into the button's own `aria-label` and the box becomes a square.
 *
 * What it was: a 30x30 box at `--r-control`, filled `--bg-sunken`, ringed `--border-2`, inked
 * `--text`, carrying a 15px funnel — four pixels shorter than the field it stands beside and drawn
 * at full strength in every state, so it read as permanently switched on and had nowhere left to go
 * when it actually was. The mock's is quiet until it is doing something:
 *
 * | | idle | panel out, or a filter set |
 * |---|---|---|
 * | ground | transparent | `--bg-chip` while the panel is out |
 * | ring | `--border` | `--border-2` |
 * | ink | `--text-2` | `--text` |
 *
 * 34x34 at radius 11 to match the field, and a 17px glyph inside it.
 *
 * The ink travels as a custom property rather than through `color` inheritance, because the glyph
 * needs it on `stroke` and the allowlist refuses `stroke: currentcolor` — it names `currentColor` in
 * camelCase and normalises the value to lower case, so the two never meet. */
.filterToggleGlyph {
  --filter-ink: var(--text-2);

  /* The dot's containing block. Without it the badge is absolutely positioned against whatever
   * ancestor happens to be positioned — in the rail that is the drawer, and the dot was drawn at the
   * top-right corner of the whole 320px panel instead of on the glyph. The mock declares the same
   * `position: relative` on its own button. */
  position: relative;
  flex: none;

  /* The glyph is the button's whole content, so it is centred in BOTH axes. The labelled variant
   * this rule narrows lays its icon and word out from the left and carries padding for it; strip
   * the padding without replacing the alignment and the 17px mark sits flush against the left
   * border of a 34px square, which is what it did. */
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: var(--space-0);
  color: var(--filter-ink);

  /* The plan library's own filter control, reused rather than drawn a second time (owner,
   * 9 September). That toolbar has carried this treatment since the programmes screen shipped, and
   * two filter buttons a click apart that do not look alike is the thing he saw. `ListToolbar`
   * fills its ground and rounds it on `--radius-mark`; the handoff's transparent square was the
   * prototype's, and it read as unfinished beside the real one. */
  background: var(--bg-chip);
  border-color: var(--border);
  border-radius: var(--radius-mark);
  transition:
    background var(--dur-fast) var(--ease-fp),
    border-color var(--dur-fast) var(--ease-fp);
}

.filterToggleGlyph .filterIcon {
  width: 17px;
  height: 17px;
  stroke: var(--filter-ink);
}

/* Both of the mock's lit conditions, and a hover between them. The mock is a static prototype and
 * draws no hover; every other control in this drawer answers the pointer with `--hover-tint`, and a
 * button that does not is the one thing on the surface that looks dead. */
.filterToggleGlyph:hover {
  background: var(--hover-tint);
}

.filterToggleGlyph[data-filtered],
.filterToggleGlyph[aria-expanded='true'] {
  --filter-ink: var(--text);

  border-color: var(--border-2);
}

.filterToggleGlyph[aria-expanded='true'] {
  background: var(--bg-chip);
}

.filterGlyphName {
  position: fixed;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* The mock's own badge on the filter GLYPH: a 7px accent dot, ringed in the button's own ground so
 * it reads as sitting on top of the glyph rather than inside it. A dot rather than a number because
 * a 34px square has nowhere to put a digit — the figure itself rides in the button's accessible
 * name, which is the channel a dot cannot use anyway.
 *
 * This is the accent used as the scalpel is meant to be used: one 7x7 mark, for a real state (the
 * catalogue is narrowed), on a screen that otherwise spends the orange on the save control. */
.filterDot {
  position: absolute;
  top: var(--space-5px);
  right: var(--space-5px);
  display: block;
  width: 7px;
  height: 7px;

  /* The mock fills it with the ember GRADIENT, not the flat accent — the same fill the primary
   * carries — and rings it in `--bg-chip` so it reads as sitting on the glyph rather than inside it.
   * `background-origin: border-box` because the ring would otherwise paint the opposite gradient
   * stop along each edge: a gradient is laid out on the padding box and painted on the border box,
   * so on a 7px dot with a 1px ring it repeats and the ring shows the wrong end of the ramp. */
  background: var(--accent-grad);
  background-origin: border-box;
  border: var(--border-hairline) solid var(--bg-chip);
  border-radius: var(--r-circle);
}

/* ── The active-filter row ───────────────────────────────────────────────────────────────── */

.activeRow {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px);
  align-items: center;
  list-style: none;
}

/* Chip and × are ONE control, because they do one thing: the gallery draws the × inside the chip
 * and there is no second act a press could mean. */
.activeChip {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-9px);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  color: var(--text);
  white-space: nowrap;
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.activeChip:hover {
  background: var(--bg-inset);
}

.activeChip:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.activeChipMark {
  font-size: var(--fs-caption);
  font-style: normal;
  font-weight: var(--fw-extrabold);
  opacity: 0.75;
}

/* "Wyczyść wszystkie" — the mock draws it as a DASHED pill rather than as bare text, which is what
 * separates "take everything off" from the solid chips that each take one thing off. It appears only
 * once there is more than one chip to clear: beside a single chip it would be a second control for
 * what that chip's own × does. */
.clearAll {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--r-pill);
}

.clearAll:hover {
  color: var(--text);
}

.clearAll:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* "9 ĆWICZEŃ" — the result count, standing directly over the list it counts. Its own row rather
 * than a line inside the header, because the mock puts it below the header's hairline. */
.countRow {
  flex: none;
  padding: var(--space-9px) var(--space-11px) var(--space-0);
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
  color: var(--text-2);
}

.dialog .countRow {
  padding: var(--space-2) var(--space-0) var(--space-0);
}

/* ── The result list ─────────────────────────────────────────────────────────────────────── */

.results {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1);
  min-height: 0;
  padding: var(--space-2) var(--space-10px);
  overflow-y: auto;
  list-style: none;
}

/* The mock's `libInner`: the drawer's own ground is the SUNKEN surface, which is what lets its rows
 * read as cards on it. It was `--bg-card` under `--bg-card` rows, so the list drew as one sheet. */
.drawer .results {
  gap: var(--space-2px);
  padding: var(--space-7px) var(--space-2);
  background: var(--bg-sunken);
}

.results:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--outline-inset);
}

/* ── The states nobody designs and everybody meets ───────────────────────────────────────── */

.notice {
  flex: 1;
  margin: var(--space-2) var(--space-10px);
  padding: var(--space-3) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-align: center;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-11px);
}

.retry {
  margin-top: var(--space-2);
  padding: var(--space-1) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  color: var(--text);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.retry:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* The empty state takes the room the list would have had, so a filter that matches nothing changes
 * the ink inside the list box and never the box itself. */
.emptyState {
  flex: 1;
  min-height: 0;
  padding: var(--space-3) var(--space-11px);
  background: var(--bg-sunken);
}

/* ── The dialog's own box ─────────────────────────────────────────────────────────────────────
 * The two columns beside a day take the height their host gives them. A modal has no such host, so
 * the list STATES its height — the same figure whichever of the four states is drawn, so a read that
 * is still running, one that failed, one that matched nothing and one that answered all occupy the
 * identical box and the dialog never resizes under the trainer's pointer.
 *
 * It is a flex BASIS that may shrink rather than a fixed height, and the filter panel is why: out,
 * it is nearly 400px tall, and a list that could not give way would push the dialog's own foot past
 * the bottom of a 900px window. Shrinking is the same answer the 320px drawer already gives — there
 * the list is `flex: 1` under a head that grows — so on a short window the two surfaces behave
 * alike. The reserve still holds wherever there is room for it, which is every state but that one. */

.dialog {
  flex: 1 1 auto;
  gap: var(--space-2);
  overflow: hidden;
}

.dialog .results,
.dialog .notice,
.dialog .emptyState {
  flex: 0 1 var(--h-library-picker-list);
  box-sizing: border-box;
  min-height: 0;
  margin: var(--space-0);
}

.dialog .results {
  gap: var(--space-2px);
  padding: var(--space-7px);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.dialog .emptyState {
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}
